import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Location } from '@core/interfaces';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { merge, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DialogLocationService } from '../../dialogs/dialog-location/dialog-locations.service';
import { FilterLocationsComponent } from '../../filter/filter-locations.component';
import { LocationService } from '../../services/location.service';
import english from './../../i18n/en.json';
import spanish from './../../i18n/es.json';
import portuguese from './../../i18n/pt.json';

@Component({
  selector: 'datatable-locations',
  templateUrl: './datatable-locations.component.html',
  styleUrls: ['datatable-locations.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DatatableLocationsComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('datatable', { read: ElementRef, static: true }) datatable: ElementRef<HTMLDivElement>;

  @Input() reload: Subject<void>;
  @Input() filters: FilterLocationsComponent;
  @Input() hasEditLocationPermission: boolean;
  @Output() totalCountLocations = new EventEmitter<number>();

  locations: Location[] = [];
  totalCount: number;
  filtered = false;
  loading = true;

  public displayedColumns: string[] = ['name', 'streetName', 'edit'];
  private subscriptions = new Subject<void>();

  constructor(
    private readonly locationService: LocationService,
    private readonly dialogLocationService: DialogLocationService,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
  }

  public async ngOnInit(): Promise<void> {
    this.loading = true;
    this.sort.active = 'name';
    this.sort.direction = 'asc';

    merge(
      this.sort.sortChange,
      this.paginator.page,
      this.reload,
      this.filters.filtersChanged.pipe(
        tap(() => this.paginator.firstPage())
      )
    )
      .pipe(
        takeUntil(this.subscriptions),
        debounceTime(1000),
        switchMap(() => {
          this.loading = true;
          this.sort.direction = this.sort.direction || 'asc';

          const options = {
            orderBy: {
              column: this.sort.active,
              direction: this.sort.direction
            },
            offset: this.paginator.pageIndex * this.paginator.pageSize,
            limit: this.paginator.pageSize
          };

          const where = this.filters.filtersChanged.value;
          return this.locationService.loadData({
            where,
            options
          });
        })
      )
      .subscribe((result) => {
        this.datatable.nativeElement.scrollTo({
          top: 0,
        });

        this.loading = false;
        this.filtered = result.filtered;
        this.locations = result.items;
        this.totalCount = result.totalCount;
        this.totalCountLocations.emit(this.totalCount);
      });

  }

  public async openDialog(locationId: string): Promise<void> {
    const result = await this.dialogLocationService.open({ locationId });
    if (result && result.reload) {
      this.reload.next();
    }
  }
}
